// PORTABLE LOGIC — pure feed normalization (imports only types; no fetch/config/DOM).
// Tolerant over the several shapes Andrew's R2 product feeds may take, including the
// nested `bands[].frames[].t` manifest. Unit-tested in scripts/verify-feed.ts.
import type { ProductFeed, ProductFrame } from './types'

const URL_KEYS = ['url', 'image', 'img', 'src', 'href', 'path', 'file']
const TIME_KEYS = ['t', 'timestamp', 'time', 'valid', 'validTime', 'datetime', 'date', 'run']
const LABEL_KEYS = ['label', 'name', 'title', 'fhr', 'step', 'band']

function pick(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v) return v
    if (typeof v === 'number') return String(v)
  }
  return undefined
}

/** Resolve a possibly-relative frame URL against the feed's origin. */
function abs(url: string, base: string): string {
  if (/^https?:\/\//.test(url)) return url
  if (url.startsWith('/')) return `${base}${url}`
  return `${base}/${url.replace(/^\.?\//, '')}`
}

/** Coerce a frame time (ISO string OR epoch seconds/ms, number or numeric string) → ISO. */
function toIso(value: string | undefined): string | undefined {
  if (value == null) return undefined
  if (/^\d+$/.test(value)) {
    const n = Number(value)
    const ms = n < 1e12 ? n * 1000 : n // <1e12 → seconds
    const d = new Date(ms)
    return Number.isNaN(d.getTime()) ? value : d.toISOString()
  }
  return value
}

function toFrame(item: unknown, base: string): ProductFrame | null {
  if (typeof item === 'string') return { url: abs(item, base) }
  if (item && typeof item === 'object') {
    const o = item as Record<string, unknown>
    const url = pick(o, URL_KEYS)
    if (!url) return null
    return { url: abs(url, base), timestamp: toIso(pick(o, TIME_KEYS)), label: pick(o, LABEL_KEYS) }
  }
  return null
}

function framesFrom(list: unknown, base: string): ProductFrame[] {
  return Array.isArray(list)
    ? list.map((f) => toFrame(f, base)).filter((f): f is ProductFrame => f !== null)
    : []
}

/**
 * Normalize any supported feed JSON into one ProductFeed.
 * @param json parsed feed body
 * @param base the feed's origin (e.g. "https://feeds.tripleaweather.com") for relative URLs
 */
export function normalizeProductFeed(json: unknown, base: string): ProductFeed {
  // Nested `bands[].frames[].t` manifest: default to the first band that has frames,
  // and surface all band names in meta for future band-switching.
  if (json && typeof json === 'object' && Array.isArray((json as Record<string, unknown>).bands)) {
    const o = json as Record<string, unknown>
    const bands = o.bands as Record<string, unknown>[]
    const bandNames = bands.map((b, i) => pick(b ?? {}, ['band', 'name', 'id', 'label']) ?? String(i))
    const chosen = bands.find((b) => Array.isArray(b?.frames) && (b.frames as unknown[]).length) ?? bands[0]
    const frames = framesFrom(chosen?.frames, base)
    return {
      frames,
      latest: frames.length ? frames[frames.length - 1] : null,
      updated: pick(o, ['updated', 'generated', ...TIME_KEYS]),
      meta: { ...o, bands: bandNames, band: pick(chosen ?? {}, ['band', 'name', 'id']) },
    }
  }

  let rawFrames: unknown[] = []
  let updated: string | undefined
  let meta: Record<string, unknown> | undefined

  if (Array.isArray(json)) {
    rawFrames = json
  } else if (json && typeof json === 'object') {
    const o = json as Record<string, unknown>
    meta = o
    updated = pick(o, ['updated', 'generated']) ?? pick(o, TIME_KEYS)
    const list =
      (o.frames as unknown[]) ??
      (o.images as unknown[]) ??
      (o.items as unknown[]) ??
      (o.products as unknown[]) ??
      (o.data as unknown[])
    if (Array.isArray(list)) rawFrames = list
    else if (pick(o, URL_KEYS)) rawFrames = [o] // single-product feed
  }

  const frames = framesFrom(rawFrames, base)
  return { frames, latest: frames.length ? frames[frames.length - 1] : null, updated, meta }
}
